import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { type CaseRecord } from "@/mocks/caseManagement";
import { caseService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";


const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  abierto: { label: "Abierto", color: "bg-secondary-100 text-secondary-700" },
  en_evaluacion: { label: "En Evaluación", color: "bg-yellow-100 text-yellow-700" },
  en_intervencion: { label: "En Intervención", color: "bg-primary-100 text-primary-700" },
  esperando_insumos: { label: "Esperando Insumos", color: "bg-orange-100 text-orange-700" },
  esperando_informe_docente: { label: "Esperando Informe Docente", color: "bg-orange-100 text-orange-700" },
  esperando_validacion: { label: "Esperando Validación", color: "bg-yellow-100 text-yellow-700" },
  informe_generado: { label: "Informe Generado", color: "bg-accent-100 text-accent-700" },
  enviado_tribunal: { label: "Enviado a Tribunal", color: "bg-green-100 text-green-700" },
  derivado_red_externa: { label: "Derivado a Red Externa", color: "bg-purple-100 text-purple-700" },
  en_seguimiento: { label: "En Seguimiento", color: "bg-blue-100 text-blue-700" },
  cerrado: { label: "Cerrado", color: "bg-background-200 text-foreground-500" },
  reabierto: { label: "Reabierto", color: "bg-red-100 text-red-700" },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  critica: { label: "Crítica", color: "text-red-600", icon: "ri-alert-fill" },
  alta: { label: "Alta", color: "text-orange-500", icon: "ri-arrow-up-circle-fill" },
  media: { label: "Media", color: "text-yellow-500", icon: "ri-subtract-fill" },
  baja: { label: "Baja", color: "text-foreground-400", icon: "ri-arrow-down-circle-fill" },
};

const KANBAN_COLUMNS = [
  { status: "abierto", label: "Abierto" },
  { status: "en_evaluacion", label: "En Evaluación" },
  { status: "en_intervencion", label: "En Intervención" },
  { status: "esperando_insumos", label: "Esperando Insumos" },
  { status: "informe_generado", label: "Informe Generado" },
  { status: "enviado_tribunal", label: "Enviado a Tribunal" },
  { status: "en_seguimiento", label: "En Seguimiento" },
  { status: "cerrado", label: "Cerrado" },
];

export default function CasosPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");

  const [cases, setCases] = useState<CaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchCases() {
      try {
        setError("");
        const data = await caseService.list();
        if (Array.isArray(data)) {
          setCases(data.map(mapDbCaseToFrontend));
        } else {
          setCases([]);
        }
      } catch (err: any) {
        console.error("Failed to fetch cases from API:", err);
        setError("Error al cargar casos desde Azure: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    fetchCases();
  }, []);

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (search && !c.titulo.toLowerCase().includes(search.toLowerCase()) && !c.studentName.toLowerCase().includes(search.toLowerCase()) && !c.folio.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && c.estado !== statusFilter) return false;
      if (categoryFilter && c.categoria !== categoryFilter) return false;
      if (priorityFilter && c.prioridad !== priorityFilter) return false;
      return true;
    });
  }, [cases, search, statusFilter, categoryFilter, priorityFilter]);

  const stats = useMemo(() => {
    const total = cases.length;
    const activos = cases.filter((c) => c.estado !== "cerrado").length;
    const criticos = cases.filter((c) => c.prioridad === "critica" && c.estado !== "cerrado").length;
    const judicializados = cases.filter((c) => c.judicializado && c.estado !== "cerrado").length;
    return { total, activos, criticos, judicializados };
  }, [cases]);

  const getSemaforo = (caso: CaseRecord) => {
    if (caso.estado === "esperando_insumos" && caso.prioridad === "critica") return "rojo";
    if (caso.prioridad === "critica" && caso.estado !== "cerrado") return "rojo";
    if (caso.estado === "esperando_insumos" || caso.estado === "esperando_informe_docente") return "amarillo";
    if (caso.estado === "enviado_tribunal" || caso.estado === "cerrado") return "verde";
    if (caso.estado === "informe_generado") return "amarillo";
    return "verde";
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Gestión de Casos</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Casos de convivencia escolar, orientación, psicología y tribunal</p>
        </div>
        <button
          onClick={() => navigate("/casos/nuevo")}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="flex items-center justify-center w-4 h-4"><i className="ri-add-line text-base" /></span>
          Abrir Nuevo Caso
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">Total Casos</p>
          <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.total}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">Casos Activos</p>
          <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.activos}</p>
        </div>
        <div className="bg-red-50/50 border border-red-200/40 rounded-xl p-4">
          <p className="text-xs text-red-600 font-medium uppercase tracking-wider">Críticos</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-2xl font-bold text-red-700">{stats.criticos}</p>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
          </div>
        </div>
        <div className="bg-purple-50/50 border border-purple-200/40 rounded-xl p-4">
          <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Judicializados</p>
          <p className="text-2xl font-bold text-purple-700 mt-1">{stats.judicializados}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-md relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-4 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            placeholder="Buscar por estudiante, folio o título..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
        >
          <option value="">Todas las categorías</option>
          <option value="convivencia">Convivencia</option>
          <option value="orientacion">Orientación</option>
          <option value="psicologia">Psicología</option>
          <option value="vulneracion">Vulneración</option>
          <option value="judicializado">Judicializado</option>
          <option value="conducta">Conducta</option>
          <option value="asistencia">Asistencia</option>
          <option value="apoderado">Apoderado</option>
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
        >
          <option value="">Todas las prioridades</option>
          <option value="critica">Crítica</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="baja">Baja</option>
        </select>
        <div className="flex items-center bg-background-100 rounded-lg p-0.5 ml-auto">
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${viewMode === "table" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500"}`}
          >
            <span className="flex items-center justify-center w-4 h-4 mr-1.5 inline-flex"><i className="ri-list-check text-sm" /></span>
            Tabla
          </button>
          <button
            onClick={() => setViewMode("kanban")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${viewMode === "kanban" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500"}`}
          >
            <span className="flex items-center justify-center w-4 h-4 mr-1.5 inline-flex"><i className="ri-layout-column-line text-sm" /></span>
            Kanban
          </button>
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-foreground-400">{filteredCases.length} caso{filteredCases.length !== 1 ? "s" : ""} encontrado{filteredCases.length !== 1 ? "s" : ""}</p>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="bg-white border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Semáforo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Folio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Estudiante</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Curso</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Título</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Categoría</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Prioridad</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Responsable</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Fecha Límite</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((caso) => {
                  const semaforo = getSemaforo(caso);
                  const statusCfg = STATUS_CONFIG[caso.estado] || STATUS_CONFIG.abierto;
                  const priorityCfg = PRIORITY_CONFIG[caso.prioridad];
                  return (
                    <tr
                      key={caso.id}
                      onClick={() => navigate(`/casos/${caso.id}`)}
                      className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-block w-3 h-3 rounded-full ${
                          semaforo === "rojo" ? "bg-red-500" : semaforo === "amarillo" ? "bg-yellow-400" : "bg-green-500"
                        }`} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-600 whitespace-nowrap">{caso.folio}</td>
                      <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{caso.studentName}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{caso.curso}</td>
                      <td className="px-4 py-3 text-foreground-900 max-w-[200px] truncate">{caso.titulo}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap capitalize">{caso.categoria}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1 text-xs font-medium ${priorityCfg.color} whitespace-nowrap`}>
                          <span className="flex items-center justify-center w-3.5 h-3.5"><i className={`${priorityCfg.icon} text-sm`} /></span>
                          {priorityCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color} whitespace-nowrap`}>{statusCfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{caso.responsableName}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{caso.fechaLimite}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredCases.length === 0 && (
            <div className="text-center py-12 text-foreground-400">
              <span className="flex items-center justify-center w-12 h-12 mx-auto mb-3"><i className="ri-folder-open-line text-3xl text-foreground-300" /></span>
              <p className="text-sm">No se encontraron casos con esos filtros</p>
            </div>
          )}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {KANBAN_COLUMNS.map((col) => {
              const colCases = filteredCases.filter((c) => c.estado === col.status);
              return (
                <div key={col.status} className="w-64 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">{col.label}</span>
                    <span className="text-xs text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full">{colCases.length}</span>
                  </div>
                  <div className="space-y-2">
                    {colCases.map((caso) => {
                      const semaforo = getSemaforo(caso);
                      const priorityCfg = PRIORITY_CONFIG[caso.prioridad];
                      return (
                        <div
                          key={caso.id}
                          onClick={() => navigate(`/casos/${caso.id}`)}
                          className="bg-white border border-background-200 rounded-lg p-3 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${
                              semaforo === "rojo" ? "bg-red-500" : semaforo === "amarillo" ? "bg-yellow-400" : "bg-green-500"
                            }`} />
                            <span className="text-xs text-foreground-400 font-mono">{caso.folio.replace("CASE-2026-", "#")}</span>
                          </div>
                          <p className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2">{caso.titulo}</p>
                          <p className="text-xs text-foreground-600 mb-2">{caso.studentName} · {caso.curso}</p>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs flex items-center gap-1 ${priorityCfg.color}`}>
                              <span className="flex items-center justify-center w-3 h-3"><i className={`${priorityCfg.icon} text-xs`} /></span>
                              {priorityCfg.label}
                            </span>
                            {caso.judicializado && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">Tribunal</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {colCases.length === 0 && (
                      <div className="text-center py-6 text-foreground-300 text-xs border border-dashed border-background-200 rounded-lg">
                        Sin casos
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}